import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Star,
  X,
  ShoppingCart,
  UsersRound,
  Gift,
  Video,
  Bell,
  Watch,
  Receipt,
  Award,
  ClipboardCheck,
  MessageCircle,
  Newspaper,
  Lightbulb,
  Stethoscope,
  Crown,
} from 'lucide-react';
import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUserProgramEnrollments } from '@/apis/users';
import Logo from '@/assets/login_logo.svg';
import HealthIcon from '@/assets/member-assets/homepage/health_icon.svg';
import HealthSnapshotIcon from '@/assets/member-assets/homepage/healthsnapshot.svg';
import HomeIcon from '@/assets/member-assets/homepage/home_black.svg';
import Logout from '@/assets/member-assets/homepage/Logout_Home.svg';
import ProductIcon from '@/assets/member-assets/homepage/product_icon.svg';
import Setting from '@/assets/member-assets/homepage/settings_home.svg';
import Support from '@/assets/member-assets/homepage/support_home.svg';
import TodoIcon from '@/assets/member-assets/homepage/todo_icon.svg';
import { clearAllThriveIntroStorage } from '@/components/member/thrive-check/thriveIntroStorage';
import { buildQuickLinksSection } from '@/components/sidebar/buildQuickLinksSection';
import { QuickLinksDrawer } from '@/components/sidebar/QuickLinksDrawer';
import { SidebarLogo } from '@/components/sidebar/SidebarLogo';
import { SidebarNavList } from '@/components/sidebar/SidebarNavList';
import { SidebarProfile } from '@/components/sidebar/SidebarProfile';
import { SidebarNavItem, ProgramEnrollment, CompactGroup } from '@/components/sidebar/types';
import { useSidebarPins } from '@/components/sidebar/useSidebarPins';
import { toTitleCase, calculateProgramProgress } from '@/components/sidebar/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FEATURE_FLAGS, isFeatureEnabled, isFeatureStrictlyEnabled } from '@/config/feature-flags';
import {
  SidebarMode,
  SIDEBAR_WIDTHS,
  getSidebarStyle,
  getSidebarBgColor,
} from '@/config/sidebar-config';
import { isThriveProgram } from '@/constants';
import { listConsultationVideos } from '@/features/videos/api';
import { normalizePaginated } from '@/features/videos/utils';
import { usePermissions } from '@/hooks/usePermissions';
import { useUiPreferences } from '@/hooks/useUiPreferences';
import { logger } from '@/lib/logger';
import { signOutSession } from '@/lib/sign-out';
import { cn } from '@/lib/utils';
import { useFeatureFlagsContext } from '@/providers/FeatureFlagsProvider';
import { RootState } from '@/redux/store';
import { Client } from '@/types/clientTypes';
import MemberDefaultModel from './member-modals/MemberDefaultModel';

interface MemberSidebarProps {
  sidebarMode: SidebarMode;
  onToggle: () => void;
  client: Client;
  isMobileOpen: boolean;
  onMobileToggle: () => void;
}

// Visible divider between the user's saved Quick Links and the rest of
// the nav. Picked up by SidebarNavList (renders a thin border row) for
// both desktop and mobile. Matches the Superclinic InternalSidebar's
// section-break behaviour.
const QUICK_LINKS_SEPARATOR: SidebarNavItem = {
  name: '--- QUICK LINKS DIVIDER ---',
  icon: Star,
  href: '#',
  isSeparator: true,
};

const getMemberNavItems = (
  clientId: string,
  badgeText: string,
  featureFlags: {
    showHealthSnapshot: boolean;
    showVideos: boolean;
    showWearables: boolean;
    showDailyLog: boolean;
    showFamilyManagement: boolean;
    showGiftMembership: boolean;
    showMemberMarketplace: boolean;
    showMemberCarts: boolean;
    showNotifications: boolean;
    showInAppChat: boolean;
    showMemberClinicalNotes: boolean;
    showMemberIdeas: boolean;
  },
  hasActiveMembership: boolean
): SidebarNavItem[] => {
  const items: SidebarNavItem[] = [];
  const p: CompactGroup = 'primary';
  const m: CompactGroup = 'more';

  items.push({
    id: 'mc-home',
    name: 'Home',
    icon: HomeIcon,
    href: `/member/home/${clientId}`,
    compactGroup: p,
  });

  if (hasActiveMembership) {
    items.push({
      id: 'mc-membership',
      name: 'Membership',
      icon: TodoIcon,
      href: `/member/todo/${clientId}`,
      badge: badgeText || '0/7',
      compactGroup: p,
    });
  } else {
    items.push({
      id: 'mc-membership',
      name: 'Membership',
      icon: TodoIcon,
      href: '',
      badge: 'Coming soon',
      disabled: true,
      compactGroup: p,
    });
  }

  if (featureFlags.showHealthSnapshot) {
    items.push({
      id: 'mc-health',
      name: 'Health Snapshot',
      compactName: 'Health',
      icon: HealthSnapshotIcon,
      href: `/member/health-snapshot/${clientId}`,
      compactGroup: p,
    });
  }

  items.push({
    id: 'mc-reports',
    name: 'Reports',
    icon: HealthIcon,
    href: `/member/reports/${clientId}`,
    compactGroup: p,
  });

  if (featureFlags.showMemberClinicalNotes) {
    items.push({
      id: 'mc-clinical-notes',
      name: "Doctor's notes",
      compactName: 'Notes',
      icon: Stethoscope,
      href: `/member/clinical-notes/${clientId}`,
      compactGroup: p,
    });
  }

  if (featureFlags.showWearables) {
    items.push({
      id: 'mc-wearables',
      name: 'Wearables',
      icon: Watch,
      href: `/member/wearables/${clientId}`,
      compactGroup: m,
    });
  }

  if (featureFlags.showDailyLog) {
    items.push({
      id: 'mc-daily-log',
      name: 'My Daily Log',
      compactName: 'Daily Log',
      icon: ClipboardCheck,
      href: `/member/daily-checkin/${clientId}`,
      compactGroup: m,
    });
  }

  if (featureFlags.showVideos) {
    items.push({
      id: 'mc-videos',
      name: 'Videos',
      icon: Video,
      href: `/member/videos/${clientId}`,
      compactGroup: m,
    });
  }

  if (featureFlags.showFamilyManagement) {
    items.push({
      id: 'mc-family',
      name: 'My Family',
      compactName: 'Family',
      icon: UsersRound,
      href: `/member/family/${clientId}`,
      compactGroup: m,
    });
  }

  if (featureFlags.showGiftMembership) {
    items.push({
      id: 'mc-gift',
      name: 'Gift a Membership',
      compactName: 'Gift',
      icon: Gift,
      href: `/member/gift-thrive-early/${clientId}`,
      compactGroup: m,
    });
  }

  if (featureFlags.showMemberMarketplace) {
    items.push({
      id: 'mc-marketplace',
      name: 'Marketplace',
      compactName: 'Market',
      icon: ProductIcon,
      href: `/member/marketplace/${clientId}`,
      compactGroup: m,
    });
    items.push({
      id: 'mc-memberships',
      name: 'Memberships',
      compactName: 'Member',
      icon: Crown,
      href: `/member/memberships/${clientId}`,
      compactGroup: m,
    });
    items.push({
      id: 'mc-purchases',
      name: 'My Purchases',
      compactName: 'Purchases',
      icon: Receipt,
      href: `/member/my-purchases/${clientId}`,
      compactGroup: m,
    });
    items.push({
      id: 'mc-benefits',
      name: 'My Benefits',
      compactName: 'Benefits',
      icon: Award,
      href: `/member/benefits/${clientId}`,
      compactGroup: m,
    });
  }

  if (featureFlags.showMemberCarts) {
    items.push({
      id: 'mc-carts',
      name: 'My Carts',
      compactName: 'Carts',
      icon: ShoppingCart,
      href: `/member/my-carts/${clientId}`,
      compactGroup: m,
    });
  }

  if (featureFlags.showNotifications) {
    items.push({
      id: 'mc-notifs',
      name: 'Notifications',
      compactName: 'Notifs',
      icon: Bell,
      href: `/member/notifications/${clientId}`,
      compactGroup: p,
    });
  }

  if (featureFlags.showInAppChat) {
    items.push({
      id: 'mc-chat',
      name: 'Chat',
      icon: MessageCircle,
      href: '/chat',
      compactGroup: m,
    });
  }

  // Public ideas / roadmap board — shared across staff and members.
  if (featureFlags.showMemberIdeas) {
    items.push({
      id: 'mc-ideas',
      name: 'Ideas',
      icon: Lightbulb,
      href: '/ideas',
      compactGroup: m,
    });
  }

  return items;
};

const getMemberMobileNavItems = (
  navItems: SidebarNavItem[],
  clientId: string,
  featureFlags: {
    showMemberWhatsNew: boolean;
  }
): SidebarNavItem[] => {
  const items = navItems.map((item) => {
    if (item.name === 'Membership' && !item.disabled && item.badge) {
      return { ...item, badgeType: 'arrow' as const };
    }
    return item;
  });

  if (featureFlags.showMemberWhatsNew) {
    items.push({ name: "What's New?", icon: Newspaper, href: `/member/whats-new/${clientId}` });
  }
  items.push({ name: 'Support', icon: Support, href: `/member/support/${clientId}` });
  items.push({ name: 'Settings', icon: Setting, href: `/member/settings/${clientId}` });

  return items;
};

const getMemberBottomNavItems = (
  clientId: string,
  showMemberWhatsNew: boolean
): SidebarNavItem[] => {
  const items: SidebarNavItem[] = [];
  if (showMemberWhatsNew) {
    items.push({
      id: 'mc-whats-new',
      name: "What's New?",
      compactName: 'New',
      icon: Newspaper,
      href: `/member/whats-new/${clientId}`,
      compactGroup: 'more' as CompactGroup,
    });
  }
  items.push({
    id: 'mc-support',
    name: 'Support',
    icon: Support,
    href: `/member/support/${clientId}`,
    compactGroup: 'primary' as CompactGroup,
  });
  items.push({
    id: 'mc-settings',
    name: 'Settings',
    icon: Setting,
    href: `/member/settings/${clientId}`,
    compactGroup: 'primary' as CompactGroup,
  });
  return items;
};

export const MemberSidebar: React.FC<MemberSidebarProps> = ({
  sidebarMode,
  onToggle,
  client,
  isMobileOpen,
  onMobileToggle,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const username = useSelector((state: RootState) => state.user.username);
  const role = useSelector((state: RootState) => state.user.role);
  const { hasPermission } = usePermissions();

  const [isLogoutModalOpen, setLogoutModalOpen] = useState(false);
  const [isQuickLinksDrawerOpen, setQuickLinksDrawerOpen] = useState(false);
  const [progressText, setProgressText] = useState('0/7');
  const [hasActiveMembership, setHasActiveMembership] = useState(false);
  const [hasConsultationVideos, setHasConsultationVideos] = useState<boolean | null>(null);

  const { flags } = useFeatureFlagsContext();
  const { data: uiPrefs } = useUiPreferences();

  const featureFlags = useMemo(
    () => ({
      showHealthSnapshot: isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_HEALTH_SNAPSHOT),
      showMemberWhatsNew: isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_MEMBER_WHATS_NEW),
      showGiftMembership: false,
      showMemberMarketplace: isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_MEMBER_MARKETPLACE),
      showFamilyManagement: isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_FAMILY_MANAGEMENT),
      showNotifications: isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_NOTIFICATIONS),
      showMemberCarts: isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_MEMBER_CARTS),
      showWearables: isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_WEARABLES),
      showDailyLog: isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_DAILY_LOG),
      showInAppChat: isFeatureStrictlyEnabled(flags, FEATURE_FLAGS.ENABLE_IN_APP_CHAT_WEBAPP),
      showMemberClinicalNotes: isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_MEMBER_CLINICAL_NOTES),
      showMemberIdeas: isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_MEMBER_IDEAS),
      isMemberVideosFeatureEnabled: isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_MEMBER_VIDEOS_TAB),
    }),
    [flags]
  );

  const clientId = client.id;

  const formattedClientName =
    `${toTitleCase(client.first_name ?? null)} ${toTitleCase(client.last_name ?? null)}`.trim() ||
    'User';

  useEffect(() => {
    const fetchProgramData = async () => {
      if (!clientId) return;

      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();
        if (!token) return;

        const response = await getUserProgramEnrollments(clientId, token);
        const enrollments = Array.isArray(response.data)
          ? response.data
          : response.data?.results || response.data?.enrollments || [];
        const hasActive = enrollments.some((enrollment: ProgramEnrollment) => enrollment.is_active);
        setHasActiveMembership(hasActive);

        const thriveEnrollment = enrollments.find(
          (enrollment: ProgramEnrollment) =>
            isThriveProgram(enrollment.program_name) && enrollment.is_active
        );

        if (thriveEnrollment?.program_data?.program_steps) {
          const progress = calculateProgramProgress(
            thriveEnrollment.program_data.program_steps,
            thriveEnrollment.program_name,
            thriveEnrollment.show_intake_call === 'true',
            client.addon_names
          );
          setProgressText(progress.progressText);
        }
      } catch (error) {
        logger.error('Error fetching program data for sidebar', error);
      }
    };

    void fetchProgramData();
  }, [clientId, client.addon_names]);

  useEffect(() => {
    if (!featureFlags.isMemberVideosFeatureEnabled) {
      setHasConsultationVideos(false);
      return;
    }

    if (!clientId) {
      setHasConsultationVideos(null);
      return;
    }

    let isCancelled = false;

    const fetchVideos = async () => {
      setHasConsultationVideos(null);
      try {
        const response = await listConsultationVideos({
          shared_only: true,
          client_id: clientId,
        });
        if (!isCancelled) {
          setHasConsultationVideos(normalizePaginated(response).length > 0);
        }
      } catch (error) {
        logger.error('Error fetching member consultation videos for sidebar', error);
        if (!isCancelled) {
          setHasConsultationVideos(true);
        }
      }
    };

    void fetchVideos();

    return () => {
      isCancelled = true;
    };
  }, [clientId, featureFlags.isMemberVideosFeatureEnabled]);

  const shouldShowVideos =
    featureFlags.isMemberVideosFeatureEnabled && hasConsultationVideos !== false;

  const navItemFlags = useMemo(
    () => ({
      ...featureFlags,
      showVideos: shouldShowVideos,
    }),
    [featureFlags, shouldShowVideos]
  );

  const baseNavItems = useMemo(
    () => getMemberNavItems(clientId, progressText, navItemFlags, hasActiveMembership),
    [clientId, progressText, navItemFlags, hasActiveMembership]
  );

  const baseBottomNavItems = useMemo(
    () => getMemberBottomNavItems(clientId, featureFlags.showMemberWhatsNew),
    [clientId, featureFlags.showMemberWhatsNew]
  );

  // Pass `flags` into buildQuickLinksSection so saved Quick Links pointing
  // at feature-flag-gated routes (Marketplace, Carts, Wearables …) get
  // filtered out when the underlying feature is disabled, matching what
  // the rest of the sidebar already does. (M2)
  // Pass the nav items so a Quick Link to a page already in the side nav
  // (Membership, Home, Reports, …) moves that entry to the top rather than
  // rendering a second copy of it.
  const quickLinksSection = useMemo(
    () =>
      buildQuickLinksSection({
        links: uiPrefs.quick_links,
        hasPermission,
        clientId,
        flags,
        navItems: [...baseNavItems, ...baseBottomNavItems],
      }),
    [uiPrefs.quick_links, hasPermission, clientId, flags, baseNavItems, baseBottomNavItems]
  );

  // Entries promoted into Quick Links leave their original position so the
  // sidebar never lists the same destination twice.
  const memberNavItems = useMemo(
    () => baseNavItems.filter((item) => !quickLinksSection.promotedHrefs.has(item.href)),
    [baseNavItems, quickLinksSection]
  );

  const memberBottomNavItems = useMemo(
    () => baseBottomNavItems.filter((item) => !quickLinksSection.promotedHrefs.has(item.href)),
    [baseBottomNavItems, quickLinksSection]
  );

  // Render an explicit divider below Quick Links whenever the user has at
  // least one saved, so a promoted entry reads as a shortcut rather than as
  // the first item of the section beneath it. (M7)
  const quickLinksWithSeparator = useMemo<SidebarNavItem[]>(
    () =>
      quickLinksSection.items.length > 0
        ? [...quickLinksSection.items, QUICK_LINKS_SEPARATOR]
        : quickLinksSection.items,
    [quickLinksSection]
  );

  const desktopNavItems = useMemo(
    () => [...quickLinksWithSeparator, ...memberNavItems, ...memberBottomNavItems],
    [quickLinksWithSeparator, memberNavItems, memberBottomNavItems]
  );

  const {
    pinnedItems,
    unpinnedItems,
    pin,
    unpin,
    moveUp,
    moveDown,
    resetToDefaults,
    pinAll,
    unpinAll,
  } = useSidebarPins(desktopNavItems, 'sidebar-pins-member');

  const pinControls = useMemo(
    () => ({
      pinnedItems,
      unpinnedItems,
      pin,
      unpin,
      moveUp,
      moveDown,
      resetToDefaults,
      pinAll,
      unpinAll,
    }),
    [pinnedItems, unpinnedItems, pin, unpin, moveUp, moveDown, resetToDefaults, pinAll, unpinAll]
  );

  const mobileNavItems = useMemo(
    () => [
      ...quickLinksWithSeparator,
      ...getMemberMobileNavItems(memberNavItems, clientId, {
        showMemberWhatsNew: featureFlags.showMemberWhatsNew,
      }),
    ],
    [quickLinksWithSeparator, memberNavItems, clientId, featureFlags.showMemberWhatsNew]
  );

  const isProfileActive = pathname.startsWith('/member/profile');

  const handleProfileClick = () => {
    if (client.id) {
      navigate(`/member/profile/${client.id}`);
    }
  };

  const handleConfirmLogout = async () => {
    try {
      clearAllThriveIntroStorage();
      await signOutSession();
    } catch (error) {
      logger.error('Error signing out', error);
    } finally {
      setLogoutModalOpen(false);
      navigate('/member/login');
    }
  };

  const handleLogoutButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setLogoutModalOpen(true);
  };

  const profileSubtitle = client.email || '';
  const widthConfig = SIDEBAR_WIDTHS[sidebarMode];
  const sidebarStyle = getSidebarStyle();
  const sidebarBgColor = getSidebarBgColor() ?? (sidebarStyle === 'light' ? undefined : '#152319');

  return (
    <>
      {/* Mobile dropdown */}
      <div
        className={cn(
          'fixed inset-0 z-50 transition-opacity duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none lg:hidden',
          isMobileOpen ? 'visible opacity-100' : 'invisible opacity-0 pointer-events-none'
        )}
        data-testid="memberSidebar_mobileOverlay"
      >
        <div
          className="absolute inset-0 bg-black/50"
          onClick={onMobileToggle}
          aria-hidden="true"
          data-testid="memberSidebar_mobileBackdrop"
        />
        <div className="relative flex h-full flex-col p-4 pt-16">
          <div
            className={cn(
              'bg-slate-100 rounded-3xl shadow-lg p-5 transition-[opacity,transform] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none max-h-[calc(100dvh-6rem)] overflow-y-auto overscroll-contain',
              isMobileOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3'
            )}
            data-testid="memberSidebar_mobileMenu"
          >
            <div className="flex items-center justify-between pb-4">
              <img src={Logo} alt="PMX Logo" className="h-7" />
              <button
                onClick={onMobileToggle}
                className="p-2.5"
                aria-label="Close menu"
                data-testid="memberSidebar_mobileCloseBtn"
              >
                <X className="w-5 h-5 text-gray-700" />
              </button>
            </div>
            <nav className="flex flex-col space-y-1 mb-4" data-testid="memberSidebar_mobileNav">
              <SidebarNavList
                items={mobileNavItems}
                pathname={pathname}
                onItemClick={onMobileToggle}
                variant="mobile"
                testIdPrefix="memberSidebar"
              />
            </nav>
            <SidebarProfile
              displayName={formattedClientName}
              subtitle={profileSubtitle}
              avatarUsername={username}
              onProfileClick={handleProfileClick}
              onLogoutClick={handleLogoutButtonClick}
              variant="mobile"
              testIdPrefix="memberSidebar"
            />
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <aside
        className={cn(
          'hidden lg:flex flex-shrink-0 flex-col border-r border-black/[0.06] transition-[width] duration-[320ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none h-[100dvh] min-h-0 overflow-hidden will-change-[width]',
          sidebarStyle === 'light' ? 'bg-[#FAF6F1]' : 'bg-primary',
          widthConfig.className
        )}
        data-sidebar-style={sidebarStyle}
        data-testid="memberSidebar_desktopSidebar"
        style={sidebarBgColor ? { backgroundColor: sidebarBgColor } : undefined}
      >
        <SidebarLogo sidebarMode={sidebarMode} onToggle={onToggle} testIdPrefix="memberSidebar" />
        <ScrollArea
          type="auto"
          className="sidebar-scroll-area min-h-0 flex-1"
          data-testid="memberSidebar_navScrollArea"
        >
          <nav
            className={cn(
              'py-2',
              sidebarMode === 'compact' ? 'space-y-0 px-2' : 'space-y-1',
              sidebarMode === 'full' ? 'px-4' : sidebarMode === 'icon-only' ? 'px-2' : ''
            )}
            aria-label="Main navigation"
            data-testid="memberSidebar_desktopNav"
          >
            <SidebarNavList
              items={desktopNavItems}
              pathname={pathname}
              sidebarMode={sidebarMode}
              onToggle={onToggle}
              variant="desktop"
              pinControls={pinControls}
              testIdPrefix="memberSidebar"
            />
          </nav>
        </ScrollArea>
        <div
          className={cn(
            'shrink-0 space-y-2 border-t border-white/10',
            sidebarMode === 'full' ? 'p-3' : 'p-2'
          )}
          data-testid="memberSidebar_bottomSection"
        >
          {role !== 'CLIENT' && (
            <button
              type="button"
              onClick={() => setQuickLinksDrawerOpen(true)}
              title="Quick links"
              aria-label="Open quick links"
              data-testid="memberSidebar_quickLinksBtn"
              className={cn(
                'w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-3xl transition-colors duration-150 ease motion-reduce:transition-none',
                sidebarStyle === 'light'
                  ? 'text-gray-700 hover:bg-black/5'
                  : 'text-white/80 hover:bg-white/10',
                sidebarMode === 'icon-only' && 'justify-center',
                sidebarMode === 'compact' && 'flex-col gap-1 px-1 py-1.5 text-[11px]'
              )}
            >
              <Star className="w-5 h-5 flex-shrink-0" />
              <span
                className={cn(
                  sidebarMode === 'compact' ? 'text-center' : 'ml-3',
                  sidebarMode === 'icon-only' && 'hidden'
                )}
              >
                Quick links
              </span>
            </button>
          )}
          <SidebarProfile
            displayName={formattedClientName}
            subtitle={profileSubtitle}
            avatarUsername={username}
            sidebarMode={sidebarMode}
            isActive={isProfileActive}
            onProfileClick={handleProfileClick}
            onLogoutClick={handleLogoutButtonClick}
            variant="desktop"
            testIdPrefix="memberSidebar"
          />
        </div>
      </aside>

      <QuickLinksDrawer
        open={isQuickLinksDrawerOpen}
        onOpenChange={setQuickLinksDrawerOpen}
        clientId={clientId}
      />

      <div data-testid="memberSidebar_logoutModal">
        <MemberDefaultModel
          isOpen={isLogoutModalOpen}
          onClose={() => setLogoutModalOpen(false)}
          onConfirm={handleConfirmLogout}
          icon={Logout}
          title="Log Out"
          description="You'll need to sign in again to access your account."
          confirmButtonText="Log Out"
          iconBgClassName="bg-gray-100"
          confirmButtonClassName="bg-black hover:bg-black"
        />
      </div>
    </>
  );
};
